import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: [
      {
        subtitle: '',
        text: 'By accessing or using the Greenly Electronics Recycling website (greenly.us) or any of our services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site. These terms apply to all visitors, users, and customers.',
      },
    ],
  },
  {
    id: 'services',
    title: '2. Description of Services',
    content: [
      {
        subtitle: 'Electronics Recycling Services',
        text: 'Greenly Electronics Recycling provides certified e-waste collection, recycling, and data destruction services throughout Los Angeles County. Services include scheduled pickup, drop-off acceptance, hard drive shredding, data destruction, and participation in community e-waste events.',
      },
      {
        subtitle: 'Service Availability',
        text: 'Our services are available to residential, commercial, and business customers within our designated service areas in Los Angeles County. We reserve the right to refuse service for items that are outside our accepted materials list, pose a safety hazard, or are otherwise ineligible for recycling under applicable regulations.',
      },
    ],
  },
  {
    id: 'accepted-materials',
    title: '3. Accepted & Prohibited Materials',
    content: [
      {
        subtitle: 'Accepted Electronics',
        text: 'We accept most consumer and commercial electronics including computers, laptops, monitors, televisions, printers, phones, tablets, batteries, and other e-waste as listed on our FAQ page. Acceptance is subject to change based on current recycling regulations and market conditions.',
      },
      {
        subtitle: 'Prohibited Items',
        text: 'We do not accept items containing hazardous materials beyond standard e-waste classifications, including but not limited to: radioactive materials, biohazardous waste, large appliances (refrigerators, washers, dryers), or items that have been contaminated with chemicals or biological agents. Submission of prohibited items may result in additional fees or refusal of service.',
      },
    ],
  },
  {
    id: 'data-destruction',
    title: '4. Data Destruction & Device Liability',
    content: [
      {
        subtitle: 'Customer Responsibility',
        text: 'Customers are solely responsible for backing up any data they wish to retain before submitting devices for recycling. By submitting a device, you acknowledge that all data on that device will be permanently destroyed and cannot be recovered.',
      },
      {
        subtitle: 'Our Data Destruction Process',
        text: 'Greenly Electronics Recycling performs physical destruction or secure wiping of all storage devices in accordance with NIST 800-88 standards. Certificates of Destruction are available upon request for business clients at no additional charge.',
      },
      {
        subtitle: 'No Liability for Data Loss',
        text: 'Greenly Electronics Recycling is not liable for any loss of data, files, software, or personal information stored on devices submitted for recycling. By using our services, you release Greenly from any claims related to data loss or device content.',
      },
    ],
  },
  {
    id: 'scheduling',
    title: '5. Scheduling & Cancellations',
    content: [
      {
        subtitle: 'Pickup Scheduling',
        text: 'Pickup appointments are subject to availability and confirmation. We will make reasonable efforts to honor scheduled appointments, but reserve the right to reschedule due to operational constraints, weather, or other circumstances beyond our control. We will provide advance notice of any changes whenever possible.',
      },
      {
        subtitle: 'Cancellations',
        text: 'Customers may cancel or reschedule a pickup appointment at any time by contacting us at least 24 hours in advance. Repeated no-shows or last-minute cancellations may result in restrictions on future scheduling.',
      },
    ],
  },
  {
    id: 'fees',
    title: '6. Fees & Charges',
    content: [
      {
        subtitle: 'Free Services',
        text: 'Many of our services, including standard residential and business pickups for qualifying electronics, are provided at no charge. Free service eligibility is subject to item type, quantity, and current program availability.',
      },
      {
        subtitle: 'Fee-Based Items',
        text: 'Certain items, including but not limited to CRT televisions, large monitors, and items requiring special handling, may be subject to a recycling fee. Applicable fees will be disclosed prior to service confirmation. Fees are non-refundable once service has been rendered.',
      },
    ],
  },
  {
    id: 'liability',
    title: '7. Limitation of Liability',
    content: [
      {
        subtitle: '',
        text: 'To the fullest extent permitted by applicable law, Greenly Electronics Recycling shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, goodwill, or other intangible losses, resulting from: (a) your use of or inability to use our services; (b) any unauthorized access to or use of our systems; (c) any interruption or cessation of our services; or (d) any errors or omissions in our content or services.',
      },
      {
        subtitle: 'Maximum Liability',
        text: 'In no event shall our total liability to you for all claims arising out of or relating to these Terms or our services exceed the amount you paid us, if any, in the twelve (12) months preceding the claim.',
      },
    ],
  },
  {
    id: 'indemnification',
    title: '8. Indemnification',
    content: [
      {
        subtitle: '',
        text: 'You agree to indemnify, defend, and hold harmless Greenly Electronics Recycling, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in any way connected with your access to or use of our services, your violation of these Terms, or your submission of prohibited or misrepresented materials.',
      },
    ],
  },
  {
    id: 'acceptable-use',
    title: '9. Acceptable Use',
    content: [
      {
        subtitle: 'Prohibited Conduct',
        text: 'You agree not to use our website or services to: submit false or misleading information; impersonate any person or entity; engage in any activity that interferes with or disrupts our services or servers; attempt to gain unauthorized access to any portion of our systems; or use our services for any unlawful purpose.',
      },
      {
        subtitle: 'Commercial Use',
        text: 'Unauthorized commercial use of our website content, branding, or service marks is strictly prohibited. Businesses wishing to partner with Greenly for bulk recycling or resale programs must obtain prior written authorization.',
      },
    ],
  },
  {
    id: 'intellectual-property',
    title: '10. Intellectual Property',
    content: [
      {
        subtitle: '',
        text: 'All content on this website, including text, graphics, logos, images, and software, is the property of Greenly Electronics Recycling or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.',
      },
    ],
  },
  {
    id: 'governing-law',
    title: '11. Governing Law & Disputes',
    content: [
      {
        subtitle: '',
        text: 'These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in Los Angeles County, California.',
      },
    ],
  },
  {
    id: 'changes',
    title: '12. Changes to These Terms',
    content: [
      {
        subtitle: '',
        text: 'We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this page. Your continued use of our services after any changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.',
      },
    ],
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: [
      {
        subtitle: '',
        text: 'If you have any questions about these Terms of Service, please contact us at: Greenly Electronics Recycling, 2542 W. 3rd Street, Los Angeles, CA 90057. Email: info@greenly.us. Phone: (213) 600-1000.',
      },
    ],
  },
];

const lastUpdated = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const TermsOfServicePage = () => (
  <>
    <title>Terms of Service | Greenly Electronics Recycling Los Angeles</title>
    <meta name="description" content="Greenly Electronics Recycling Terms of Service — service agreements, liability limitations, acceptable use & data destruction policies. Los Angeles, CA." />
    <meta name="robots" content="index, follow" />
    <meta name="last-modified" content="2026-05-11" />
    <link rel="canonical" href={`${SITE_URL}/terms-of-service`} />
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/terms-of-service#webpage`,
          "url": `${SITE_URL}/terms-of-service`,
          "name": "Terms of Service | Greenly Electronics Recycling Los Angeles",
          "description": "Read Greenly Electronics Recycling's Terms of Service — service agreements, liability limitations, acceptable use, and data destruction policies. Los Angeles, CA.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "about": { "@id": `${SITE_URL}/#business` },
          "breadcrumb": { "@id": `${SITE_URL}/terms-of-service#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/terms-of-service#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Terms of Service", "item": `${SITE_URL}/terms-of-service` }
          ]
        }
      ]
    })}</script>
    <Navbar />
    <main>
      {/* Hero */}
      <section className="pt-16" style={{ background: 'linear-gradient(135deg, #f0faf4 0%, #e8f5ee 40%, #f5fbf7 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#1B6B3A] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-700 font-medium">Terms of Service</span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0 mt-1">
              <i className="ri-file-text-line text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
                Terms of <span className="text-[#1B6B3A]">Service</span>
              </h1>
              <p className="text-gray-500 text-sm">Last Updated: {lastUpdated} &nbsp;·&nbsp; Effective: January 1, 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Sidebar TOC */}
            <aside className="lg:w-64 shrink-0">
              <div className="sticky top-24">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">On This Page</p>
                <nav className="space-y-1.5">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-500 hover:text-[#1B6B3A] transition-colors cursor-pointer leading-snug py-0.5"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
                <div className="mt-8 p-4 bg-[#E8F5EE] rounded-xl">
                  <p className="text-xs text-[#1B6B3A] font-semibold mb-2">Questions?</p>
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">Contact us with any questions about our service terms.</p>
                  <a
                    href="mailto:info@greenly.us"
                    className="inline-flex items-center gap-1.5 text-xs text-[#1B6B3A] font-bold hover:underline cursor-pointer"
                  >
                    <i className="ri-mail-line"></i>
                    info@greenly.us
                  </a>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 font-semibold mb-2">Related Policies</p>
                  <Link to="/privacy-policy" className="flex items-center gap-1.5 text-xs text-[#1B6B3A] hover:underline cursor-pointer">
                    <i className="ri-shield-check-line"></i>
                    Privacy Policy
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="bg-[#E8F5EE] border border-[#c8e6d4] rounded-xl px-5 py-4 mb-10 flex gap-3 items-start">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-[#1B6B3A]"></i>
                </div>
                <p className="text-sm text-[#1B6B3A] leading-relaxed">
                  Please read these Terms of Service carefully before using our website or services. By using Greenly Electronics Recycling services, you agree to be bound by these terms.
                </p>
              </div>

              <div className="space-y-10">
                {sections.map((section) => (
                  <div key={section.id} id={section.id} className="scroll-mt-28">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">{section.title}</h2>
                    <div className="space-y-4">
                      {section.content.map((block, idx) => (
                        <div key={idx}>
                          {block.subtitle && (
                            <h3 className="text-sm font-semibold text-gray-800 mb-1.5">{block.subtitle}</h3>
                          )}
                          <p className="text-gray-600 text-sm leading-relaxed">{block.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-14 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">Questions about our terms?</p>
                  <p className="text-xs text-gray-500">We&apos;re happy to clarify anything before you use our services.</p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default TermsOfServicePage;
